import { useState, useEffect } from "react";
import { TrophyIcon, XMarkIcon } from "@heroicons/react/24/solid";
import axios from "axios";

export default function LiveWinNotifications() {
  const [winData, setWinData] = useState([]);
  const [currentWinIndex, setCurrentWinIndex] = useState(0);
  const [showNotification, setShowNotification] = useState(true);
  const [notificationVisible, setNotificationVisible] = useState(true);
  const [exitAnimation, setExitAnimation] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch real win data from API
  useEffect(() => {
    const fetchRecentWins = async () => {
      try {
        setLoading(true);
        
        // Get JWT token (or user can fall back to demo mode)
        const token = localStorage.getItem('token');
        if (!token) {
          setError("No authentication token found. Using limited data.");
          setLoading(false);
          return;
        }
        
        // Fetch the most recent casino wins
        const response = await axios.get('/api/casino/recent-wins', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (response.data && response.data.wins && response.data.wins.length > 0) {
          setWinData(response.data.wins);
        } else {
          setError("No recent win data available.");
        }
      } catch (err) {
        console.error('Failed to load recent win data:', err);
        setError(err.message || "Failed to load win data");
      } finally {
        setLoading(false);
      }
    };
    
    fetchRecentWins();
    
    // Set up refresh interval - fetch new data every 2 minutes
    const refreshInterval = setInterval(fetchRecentWins, 120000);
    
    return () => clearInterval(refreshInterval);
  }, []);
  
  // Rotate through win notifications
  useEffect(() => {
    if (!showNotification || winData.length === 0) return;
    
    // Set up rotation timer for notifications
    const rotationTimer = setInterval(() => {
      setExitAnimation(true);
      
      setTimeout(() => {
        setCurrentWinIndex(prev => (prev + 1) % winData.length);
        setExitAnimation(false);
      }, 500); // Match with CSS transition time
    }, 4000);
    
    return () => clearInterval(rotationTimer);
  }, [winData.length, showNotification, currentWinIndex]);
  
  // Format time like "2m ago", "Just now", etc.
  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diffSeconds = Math.floor((now - new Date(timestamp)) / 1000);
    
    if (diffSeconds < 5) return "Just now";
    if (diffSeconds < 60) return `${diffSeconds}s ago`;
    
    const diffMinutes = Math.floor(diffSeconds / 60);
    if (diffMinutes < 60) return `${diffMinutes}m ago`;
    
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };
  
  // Return nothing if no data or notification is hidden
  if (winData.length === 0 || !notificationVisible) return null;
  
  // Show loading state
  if (loading) return null;
  
  const currentWin = winData[currentWinIndex];
  
  // Determine if it's a jackpot based on multiplier or set criteria
  const isJackpot = currentWin.multiplier >= 50 || currentWin.payout >= 500;
  
  // Map game names to appropriate emojis
  const gameIcons = {
    'fruit-scatter': '🍒',
    'blackjack': '🃏',
    'roulette': '🎲',
    'poker': '♠️',
    'slots': '🎰'
  };
  
  // Get appropriate icon for the game
  const gameIcon = gameIcons[currentWin.gameName] || gameIcons[currentWin.gameType] || '🎰';
  
  return (
    <div className={`fixed bottom-4 left-4 z-40 transition-all duration-500 ease-in-out transform 
      ${exitAnimation ? 'opacity-0 -translate-y-2' : 'opacity-100 translate-y-0'}
      ${!showNotification ? 'hidden' : ''}`}>
      <div className={`bg-gradient-to-r from-gray-900 to-gray-800 rounded-lg shadow-xl border border-gray-700 p-2 max-w-xs
        ${isJackpot ? 'animate-pulse border-yellow-500' : ''}`}>
        <div className="flex items-center">
          {/* Close button */}
          <button 
            onClick={() => {
              setNotificationVisible(false);
            }}
            className="absolute -top-2 -right-2 bg-gray-800 rounded-full p-1 border border-gray-700 text-gray-400 hover:text-gray-200 transition-colors"
          >
            <XMarkIcon className="h-3 w-3" />
          </button>
          
          {/* Game icon */}
          <div className={`h-10 w-10 rounded-full flex items-center justify-center mr-2
            ${isJackpot 
              ? 'bg-gradient-to-r from-yellow-600 to-amber-600 border border-yellow-400' 
              : 'bg-gradient-to-r from-pink-600 to-purple-600'}`}>
            <span className="text-xl">{gameIcon}</span>
          </div>
          
          <div className="flex-1">
            <div className="flex items-center">
              <span className="text-white text-xs font-semibold mb-0.5 truncate">
                {currentWin.username} won
              </span>
              {isJackpot && (
                <span className="ml-1 bg-yellow-600 text-yellow-200 text-xs px-1 rounded">JACKPOT</span>
              )}
            </div>
            <div className={`${isJackpot ? 'text-yellow-300' : 'text-green-400'} text-sm font-bold`}>
              {currentWin.payout} {currentWin.payout === 1 ? "Chip" : "Chips"}
            </div>
          </div>
          
          <div className={`ml-2 text-xs ${
            isJackpot 
              ? 'bg-yellow-900/50 border border-yellow-800 text-yellow-400' 
              : 'bg-green-900/50 border border-green-800 text-green-400'
          } rounded px-1.5 py-0.5 flex items-center`}>
            {currentWin.multiplier >= 50 && <TrophyIcon className="h-3 w-3 mr-1 text-yellow-400" />}
            {currentWin.multiplier}× Win
          </div>
        </div>
        
        {/* Timestamp */}
        <div className="mt-1 text-right text-xs text-gray-500">
          {formatTimeAgo(currentWin.timestamp || currentWin.createdAt)}
        </div>
      </div>
    </div>
  );
}
